import type { CustomEmployeeField } from '@/app/features/control-panel/organization';
import { useActiveCustomFields } from '../shared/custom-fields.helpers';
import type { EmployeeData } from '@/app/features/employee/emp-dashboard/emp-dashboard.type';
import { formatDate } from '@/utils';
import { Empty } from 'antd';

type Props = {
  employee?: EmployeeData;
  loading?: boolean;
};

/** Renders one stored value the way its field type says it should read. */
const displayValue = (field: CustomEmployeeField, value: unknown) => {
  if (value === undefined || value === null || value === '') return '--';
  if (field.field_type === 'SWITCH') return value ? 'Yes' : 'No';
  if (field.field_type === 'DATE') return formatDate(value as string, 'DD MMM, YYYY');
  return String(value);
};

const InfoItem = ({ label, value }: { label: string; value: string }) => (
  <div className="space-y-1">
    <p className="text-sm text-white/45">{label}</p>
    <p className="text-base text-white break-words">{value}</p>
  </div>
);

/**
 * Employee details → Additional Information.
 *
 * Shows the company's own extra fields, all together in one place so they are
 * never confused with the built-in employee information. Nothing defined means
 * nothing to show, and the tab says so instead of rendering an empty shell.
 */
const AdditionalInfo = ({ employee, loading }: Props) => {
  const { fields, isLoading } = useActiveCustomFields();
  const values = (employee?.custom_fields || {}) as Record<string, unknown>;

  if (loading || isLoading) {
    return (
      <div className="bg-theme-slate-800 rounded-xl p-6 animate-pulse space-y-4">
        <div className="h-6 bg-theme-slate-700 rounded w-1/4" />
        <div className="h-5 bg-theme-slate-700 rounded w-1/2" />
        <div className="h-5 bg-theme-slate-700 rounded w-1/3" />
      </div>
    );
  }

  if (!fields.length) {
    return (
      <div className="bg-theme-slate-800 rounded-xl p-10">
        <Empty
          description={
            <span className="text-white/45">
              No custom fields yet. Add them in Control Panel → Organization →
              Custom Fields.
            </span>
          }
        />
      </div>
    );
  }

  return (
    <div className="bg-theme-slate-800 rounded-xl p-6">
      <div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
        {fields.map((field) => (
          <InfoItem
            key={field.id}
            label={field.field_name}
            value={displayValue(field, values[field.field_name])}
          />
        ))}
      </div>
    </div>
  );
};

export default AdditionalInfo;
